import React, { useEffect, useState, useRef, createContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onIdTokenChanged } from 'firebase/auth';
import { auth, db, functions } from './firebase';
import { doc, getDoc, getDocFromServer, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import LandingPage from './landing/LandingPage';
import Login from './auth/Login';
import Signup from './auth/Signup';
import ForgotPassword from './auth/ForgotPassword';
import Artists from './artists/Artists';
import Home from './home/Home';
import ProductDetail from './products/ProductDetail';
import ProductPurchasePreview from './products/ProductPurchasePreview';
import UnitDetail from './units/UnitDetail';
import UnitPurchaseSuccess from './units/UnitPurchaseSuccess';
import UpdateArtist from './creator/UpdateArtist';
import Payment from './history/Payment';
import Creator from './creator/Creator';
import CreatorProducts from './creator/CreatorProducts';
import DropWizard from './creator/DropWizard';
import DropDetail from './creator/DropDetail';
import ProductScheduleEditor from './creator/ProductScheduleEditor';
import Terms from './terms/Terms';
import Services from './services/Services';
import Fan from './fan/Fan';
import FanFollowing from './fan/FanFollowing';
import FanSettings from './fan/FanSettings';
import Transactions from './transactions/Transactions';
import TransactionDetail from './transactions/TransactionDetail';
import { detectInitialUserSettings } from './utils/userSettings';
import Account from './account';
import ChangeName from './account/ChangeName';
import ChangeEmail from './account/ChangeEmail';
import ChangePassword from './account/ChangePassword';
import AdminShell from './admin/AdminShell';
import ArtistApplicationsAdmin from './admin/ArtistApplicationsAdmin';
import PaymentsAdmin from './admin/PaymentsAdmin';
import TemporaryArtistsAdmin from './admin/TemporaryArtistsAdmin';
import ProtectedRoute from './ProtectedRoute';
import RouteSeo from './seo/RouteSeo';
import { hasAdminAccess } from './utils/admin';
import { hasRoleEntitlement, ROLE_ARTIST } from './utils/roles';
import { resolveReturnTo } from './auth/returnTo';

export const UserContext = createContext(null);

function ArtistRoute({ user, appUser, checkingProfile, children }) {
  if (!user) return <Navigate to="/signin" />;
  if (checkingProfile || !appUser) return null;
  return hasRoleEntitlement(appUser, ROLE_ARTIST) ? children : <Navigate to="/payment" replace />;
}

function AdminAreaRoute({ user, appUser, area, children }) {
  if (!user) return <Navigate to="/signin" />;
  return hasAdminAccess(appUser, area) ? children : <Navigate to="/admin" replace />;
}

function AuthPageRoute({ user, children }) {
  const location = useLocation();
  if (user) return <Navigate to={resolveReturnTo({ location, fallback: '/home' })} replace />;
  return children;
}

function AppRoutes({ user, appUser, checkingProfile }) {
  return (
    <>
      <ScrollRestoration />
      <RouteSeo />
      <Routes>
        <Route path="/" element={user ? <Navigate to="/home" /> : <LandingPage />} />
        <Route path="/signin" element={<AuthPageRoute user={user}><Login /></AuthPageRoute>} />
        <Route path="/signup" element={<AuthPageRoute user={user}><Signup /></AuthPageRoute>} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="/artists/:artistId" element={<Artists />} />
        <Route path="/products/:productId" element={<ProductDetail />} />
        <Route path="/products/:productId/preview" element={<ProtectedRoute><ProductPurchasePreview /></ProtectedRoute>} />
        <Route path="/products" element={<Navigate to="/home" replace />} />
        <Route path="/units/:unitId" element={<ProtectedRoute><UnitDetail /></ProtectedRoute>} />
        <Route path="/units/:unitId/success" element={<ProtectedRoute><UnitPurchaseSuccess /></ProtectedRoute>} />

        <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/artists" element={<Navigate to="/home" replace />} />
        <Route path="/fan" element={<ProtectedRoute><Fan /></ProtectedRoute>} />
        <Route path="/fan/following" element={<ProtectedRoute><FanFollowing /></ProtectedRoute>} />
        <Route path="/fan/settings" element={<ProtectedRoute><FanSettings /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><Transactions /></ProtectedRoute>} />
        <Route path="/transactions/:transactionId" element={<ProtectedRoute><TransactionDetail /></ProtectedRoute>} />
        <Route path="/fan/collectibles" element={<Navigate to="/fan" replace />} />
        <Route path="/creator" element={<ArtistRoute user={user} appUser={appUser} checkingProfile={checkingProfile}><Creator /></ArtistRoute>} />
        <Route path="/creator/profile" element={<ArtistRoute user={user} appUser={appUser} checkingProfile={checkingProfile}><UpdateArtist /></ArtistRoute>} />
        <Route path="/creator/products" element={<ArtistRoute user={user} appUser={appUser} checkingProfile={checkingProfile}><CreatorProducts /></ArtistRoute>} />
        <Route path="/creator/drops/new" element={<ArtistRoute user={user} appUser={appUser} checkingProfile={checkingProfile}><DropWizard /></ArtistRoute>} />
        <Route path="/creator/drops/:dropId" element={<ArtistRoute user={user} appUser={appUser} checkingProfile={checkingProfile}><DropDetail /></ArtistRoute>} />
        <Route path="/creator/products/new" element={<Navigate to="/creator/drops/new" replace />} />
        <Route path="/creator/products/:productId/edit" element={<ArtistRoute user={user} appUser={appUser} checkingProfile={checkingProfile}><ProductScheduleEditor /></ArtistRoute>} />
        <Route path="/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        <Route path="/account/name" element={<ProtectedRoute><ChangeName /></ProtectedRoute>} />
        <Route path="/terms" element={<ProtectedRoute><Terms /></ProtectedRoute>} />
        <Route path="/services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
        <Route path="/account/email" element={<ProtectedRoute><ChangeEmail /></ProtectedRoute>} />
        <Route path="/account/password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminShell /></ProtectedRoute>} />
        <Route path="/admin/artist-applications" element={<AdminAreaRoute user={user} appUser={appUser} area="artists"><ArtistApplicationsAdmin /></AdminAreaRoute>} />
        <Route path="/admin/temporary-artists" element={<AdminAreaRoute user={user} appUser={appUser} area="artists"><TemporaryArtistsAdmin /></AdminAreaRoute>} />
        <Route path="/admin/payments" element={<AdminAreaRoute user={user} appUser={appUser} area="payments"><PaymentsAdmin /></AdminAreaRoute>} />
        <Route path="/tickets/:ticketId" element={<Navigate to="/home" replace />} />
        <Route path="/fan/tickets" element={<Navigate to="/fan" replace />} />
        <Route path="/events/:eventId" element={<Navigate to="/home" replace />} />
        <Route path="/venue" element={<Navigate to="/home" replace />} />
        <Route path="/venue/profile" element={<Navigate to="/home" replace />} />
        <Route path="/venue/events/new" element={<Navigate to="/home" replace />} />
        <Route path="/venue/events/:eventId" element={<Navigate to="/home" replace />} />
        <Route path="*" element={<Navigate to={user ? '/home' : '/'} />} />
      </Routes>
    </>
  );
}

function ScrollRestoration() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const shouldPreserveScroll = (() => {
      if (pathname === '/') return typeof window.history.state?.landingScrollY === 'number';
      if (pathname === '/home') {
        try {
          const stored = window.sessionStorage.getItem('homeSlideState');
          if (!stored) return false;
          const parsed = JSON.parse(stored);
          return typeof parsed?.scrollY === 'number';
        } catch {
          return false;
        }
      }
      return false;
    })();
    if (!shouldPreserveScroll) window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

function App() {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [appUser, setAppUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const ensureStripeCustomerPromiseRef = useRef(null);

  useEffect(() => onIdTokenChanged(auth, (nextUser) => {
    setFirebaseUser(nextUser);
    setCheckingAuth(false);
  }), []);

  useEffect(() => {
    if (!firebaseUser) {
      setAppUser(null);
      setCheckingProfile(false);
      return undefined;
    }
    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      try {
        setCheckingProfile(true);
        const userRef = doc(db, 'users', firebaseUser.uid);
        let snap = await getDoc(userRef);
        const initialSettings = await detectInitialUserSettings();
        if (!snap.exists()) {
          const baseProfile = {
            uid: firebaseUser.uid,
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            firstName: '',
            lastName: '',
            photoPath: '',
            photoThumbPath: '',
            profileVisibility: 'public',
            primaryAuthUid: firebaseUser.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            adminGroups: [],
            roles: { fan: true },
            subscriptions: {
              artist: { isActive: false, status: 'inactive', hasEverSubscribed: false, activatedAt: null, endedAt: null },
            },
            artistApprovalApproved: false,
            artistApplicationStatus: 'not_started',
            artistApplicationSubmittedAt: null,
            artistApplicationLastId: null,
            settings: initialSettings,
          };
          await setDoc(userRef, baseProfile, { merge: true });
          snap = await getDocFromServer(userRef);
        } else {
          const pendingUpdates = {};
          if (!snap.data()?.settings?.currency || !snap.data()?.settings?.timezone) {
            pendingUpdates.settings = {
              ...(snap.data()?.settings || {}),
              ...initialSettings,
            };
          }
          if (typeof snap.data()?.artistApprovalApproved !== 'boolean') pendingUpdates.artistApprovalApproved = false;
          if (typeof snap.data()?.artistApplicationStatus !== 'string') pendingUpdates.artistApplicationStatus = 'not_started';
          if (!Object.prototype.hasOwnProperty.call(snap.data() || {}, 'artistApplicationSubmittedAt')) pendingUpdates.artistApplicationSubmittedAt = null;
          if (!Object.prototype.hasOwnProperty.call(snap.data() || {}, 'artistApplicationLastId')) pendingUpdates.artistApplicationLastId = null;
          if (String(firebaseUser.email || '').trim() && String(snap.data()?.email || '').trim() !== String(firebaseUser.email || '').trim()) {
            pendingUpdates.email = String(firebaseUser.email || '').trim();
          }
          if (Object.keys(pendingUpdates).length) {
            pendingUpdates.updatedAt = serverTimestamp();
            await setDoc(userRef, pendingUpdates, { merge: true });
            snap = await getDocFromServer(userRef);
          }
        }

        unsubscribe = onSnapshot(userRef, (docSnap) => {
          if (!docSnap.exists()) return;
          const data = docSnap.data() || {};
          if (!cancelled) {
            setAppUser({
              ...data,
              id: firebaseUser.uid,
              uid: firebaseUser.uid,
              adminGroups: Array.isArray(data.adminGroups) ? data.adminGroups : [],
              roles: {
                fan: true,
                ...(data.roles || {}),
              },
              subscriptions: {
                artist: data.subscriptions?.artist || { isActive: false, status: 'inactive', hasEverSubscribed: false, activatedAt: null, endedAt: null },
              },
              artistApprovalApproved: typeof data.artistApprovalApproved === 'boolean' ? data.artistApprovalApproved : false,
              artistApplicationStatus: typeof data.artistApplicationStatus === 'string' ? data.artistApplicationStatus : 'not_started',
              artistApplicationSubmittedAt: data.artistApplicationSubmittedAt || null,
              artistApplicationLastId: data.artistApplicationLastId || null,
            });
            setCheckingProfile(false);
          }
        });

        if (!ensureStripeCustomerPromiseRef.current) {
          const callable = httpsCallable(functions, 'ensureStripeCustomer');
          ensureStripeCustomerPromiseRef.current = callable({}).catch(() => null).finally(() => {
            ensureStripeCustomerPromiseRef.current = null;
          });
        }
      } catch {
        if (!cancelled) setCheckingProfile(false);
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [firebaseUser]);

  if (checkingAuth || checkingProfile) return null;

  return (
    <UserContext.Provider value={appUser}>
      <Router>
        <AppRoutes user={firebaseUser} appUser={appUser} checkingProfile={checkingProfile} />
      </Router>
    </UserContext.Provider>
  );
}

export default App;
